"use strict";
import axios, { AxiosResponse } from "axios";
import {
	APICalls,
	getPayloadAsync,
	getXplanURL,
	trimChar,
	appConfig,
	handleResponseData,
	formatDate,
	isEmpty,
	getLoginUserName,
	updateCredentials,
	getLoginUserId,
	attachnmentTimeout,
	isNetworkError,
} from "./helper";

import { LogError } from "../actions/errorWorker";
import { toggleLoading, successScreen, errorScreen } from "./navigationWorker";
import { getAccessToken } from "../../msal-auth/auth";

const attachmentList = {};

export class XplanFiler {
	data = {};
	body = {};
	url = "";
	docId = null;
	target = "";
	ExistingDocId = "0";

	constructor(url: string) {
		this.data = {};
		this.body = {};
		this.url = url;
		this.target = "XPLAN";
		this.ExistingDocId = "0";
	}

	public async fileEmailToXplan(otherData: any) {
		if (!Office.context.mailbox) throw new Error("No Email Object");

		this.setRedirectUrl(
			getXplanURL(),
			otherData.entity_ids[0],
			otherData.fileToType,
			otherData.role
		);

		attachmentList["attachmentToken"] = await getAccessToken();
		attachmentList["itemid"] = this.getItemRestId();

		this.data["subject"] =
			otherData.subject === "" ? Office.context.mailbox.item.subject : otherData.subject;

		if (otherData.fileToType === "newtask") {
			this.data["taskkind"] = Number(otherData.tasktype);
			this.data["subtype"] = Number(otherData.tasksubtype);
			this.data["status"] = Number(otherData.taskstatus);
			this.data["description"] = Office.context.mailbox.item["subject"];
			// Add assignee if provided, otherwise backend will assign to current user
			if (otherData.assignee) {
				this.data["assignee"] = Number(otherData.assignee);
			}
		} else if (otherData.fileToType === "activetask") {
			this.data["active_tasks"] =
				JSON.parse(sessionStorage.getItem("selected_active_tasks")) || [];
		}

		// This method should only handle tasks, notes go through fileNoteToXplan
		const url = this.url;
		await this.sendTaskToXplan(url, this.data);
	}

	public async fileNoteToXplan(input: any, id = "0"): Promise<void> {
		const url = this.url + (id === "0" ? "" : `/${id}`);
		const method = !id || id === "0" ? "POST" : "PATCH";
		const payload = await getPayloadAsync(url, null, method);
		const subject = input.subject || Office.context.mailbox.item.subject;
		const excludeEmail = sessionStorage.getItem("exclude_email_from_note") === "true";
		const customNoteBody = sessionStorage.getItem("custom_note_body") || "";
		
		// Use custom note date if provided, otherwise use email date
		const noteDate = input.note_date ? new Date(input.note_date) : Office.context.mailbox.item.dateTimeCreated;

		console.log("Filing note - excludeEmail:", excludeEmail, "customNoteBody:", customNoteBody);

		this.setRedirectUrl(getXplanURL(), input.entity_ids[0], input.fileToType, input.role);

		try {
			const data: any = {
				subject,
				body: excludeEmail ? {
					_type: "Binary",
					_val: btoa(unescape(encodeURIComponent(customNoteBody)))
				} : "",
				ext_type: excludeEmail ? "" : "OutlookAddInV2",
				ext_item_id: excludeEmail ? "" : Office.context.mailbox.item["conversationId"],
				ext_sub_item_id: excludeEmail ? "" : Office.context.mailbox.item["itemId"],
				mimetype: "text/html",
				reference_date: formatDate(noteDate),
				metadata: this.generateMetaData(),
				...(method === "PATCH"
					? {
							ext_type: "",
							ext_item_id: "",
							ext_sub_item_id: "",
					  }
					: {}),
				...(this.ExistingDocId === "0"
					? {
							subject: input.subject,
							doctype: input.doctype,
							docsubtype: input.docsubtype,
							entity_ids: input.entity_ids.slice(),
							permission: input.permission,
							categories: input.categories,
							is_client_access_accessible: input.is_client_access_accessible,
							is_referrer_accessible: input.is_referrer_accessible,
							is_profadviser_accessible: input.is_profadviser_accessible,
					  }
					: {}),
			};

			payload.data = data;
			payload.target = excludeEmail ? "XPLAN" : "EWSXPLAN";
			payload.method = method;
			payload.accessToken = excludeEmail ? "" : await getAccessToken();
			payload.restUrl = excludeEmail ? "" : APICalls.MicrosoftGraph;
			payload.itemId = excludeEmail ? "" : trimChar(this.getItemRestId(), "/");
			payload.is_task = false;
			payload.subject = subject;
			payload.user_id = getLoginUserId();
			payload.user_message_url = getXplanURL() + APICalls.UserMessage;
			payload.user_email_address = this.getCorrectToAddress();
			payload.mailbox_owner_email = excludeEmail ? "" : await this.getMailboxOwnerEmail();

			// file email + attachments
			const response = await axios.post(APICalls.AWSUrl(), payload);

			this.logApiResponse(response, "SendToXplan Response");

			const resData = handleResponseData(response, "Failed to file to Xplan.");

			console.log("Response data:", resData);

			if (resData) {
				// update access credentials
				updateCredentials(response, resData);

				if (resData.isError === true) {
					let message = response.data.message || resData.message || "An error occurred while filing";
					if (response.data?.message?.includes(attachnmentTimeout))
						message = message.slice(43);
					LogError(resData, { payload }, "getCurrentItems", "ERROR");
					return errorScreen(false, "", "", message);
				} else {
					return successScreen(
						false,
						sessionStorage.getItem("redirectUrl"),
						"Go to Notes",
						resData.message
					);
				}
			}

			return errorScreen(
				false,
				"",
				"",
				"An unexpected error has occured. Please try again later."
			);
		} catch (error) {
			toggleLoading(false);

			let message = error.message;

			// Check if this is an HTTP error response (like 400, 500, etc.)
			if (error.response) {
				const resData = handleResponseData(error.response, "Failed to file to Xplan.");
				console.log("Error response data:", resData);
				
				if (resData && resData.isError === true) {
					let errorMessage = error.response.data?.message || resData.message || "An error occurred while filing";
					LogError(resData, {}, "fileNoteToXplan", "ERROR");
					return errorScreen(false, "", "", errorMessage);
				}
			}

			// Only show success for actual network timeouts, not HTTP errors
			if (message === "Network Error" || isNetworkError(error)) {
				return successScreen(
					false,
					sessionStorage.getItem("redirectUrl"),
					"Go to Notes",
					attachnmentTimeout
				);
			}

			if (error.isAxiosError && error.response.status === 413) {
				message =
					"Your email content exceeds the max limit of 10MB. Please remove the unnecessary content/images and try again.";
			}

			if (error.isAxiosError)
				this.logApiResponse(error.response, "SendToXplan Error", payload);
			else LogError(error, { payload }, "sendToXplan", "ERROR");

			errorScreen(false, null, null, message);
		}
	}

	public async fileExistingNoteToXplan(docId: string, input: any) {
		return this.fileNoteToXplan(input, docId);
	}

	public escapeUnicode(str: string) {
		if (isEmpty(str)) return str;
		return str.replace(/[^\x00-\x7F]+/g, (ch) => {
			return "\\u" + ("000" + ch.charCodeAt(0).toString(16)).slice(-4);
		});
	}

	getItemRestId(): string {
		const id =
			Office.context.mailbox.diagnostics.hostName === "OutlookIOS"
				? Office.context.mailbox.item.itemId
				: Office.context.mailbox.convertToRestId(
						Office.context.mailbox.item.itemId,
						Office.MailboxEnums.RestVersion.v2_0
				  );

		return encodeURIComponent(id);
	}

	async getAccessToken(): Promise<string> {
		return new Promise((resolve, reject) => {
			Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, (result) => {
				if (result.status !== Office.AsyncResultStatus.Succeeded)
					return reject(result.error);

				let token = result.value as any;
				if (typeof result.value !== "string") token = token?.accessTpoken;

				resolve(token);
			});
		});
	}

	private generateMetaData(): Record<string, any> {
		return {
			conversationid: Office.context.mailbox.item["conversationId"],
			conversationindex: Office.context.mailbox.item["itemId"],
			"filed-by": getLoginUserName(),
			"filed-by-address": this.getCorrectToAddress(),
			"email-date": formatDate(Office.context.mailbox.item.dateTimeCreated),
			"email-from": Office.context.mailbox.item["sender"].emailAddress,
			"email-from-address": Office.context.mailbox.item["sender"].emailAddress,
			"email-to": this.arrayToString(Office.context.mailbox.item["to"], "emailAddress", ";"),
			"email-to-address": this.arrayToString(
				Office.context.mailbox.item["to"],
				"emailAddress",
				";"
			),
			"email-cc": this.arrayToString(Office.context.mailbox.item["cc"], "emailAddress", ";"),
		};
	}

	private arrayToString(arrayData: Array<object>, field: string, delimit: string): string {
		return arrayData.map((item) => item[field]).join(delimit);
	}

	private getCorrectToAddress(): string {
		return Office.context.mailbox.userProfile.emailAddress;
	}

	/**
	 * Get the actual mailbox owner email (handles shared/delegate mailboxes)
	 * For shared mailboxes: returns the shared mailbox email
	 * For own mailbox: returns the user's email
	 */
	private async getMailboxOwnerEmail(): Promise<string> {
		return new Promise((resolve) => {
			try {
				// Check if this is a shared mailbox
				if (Office.context.mailbox?.item?.getSharedPropertiesAsync) {
					Office.context.mailbox.item.getSharedPropertiesAsync((result) => {
						if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.targetMailbox) {
							// This is a shared mailbox - return the shared mailbox email
							resolve(result.value.targetMailbox);
						} else {
							// Not a shared mailbox or failed - return user's email
							resolve(Office.context.mailbox.userProfile.emailAddress);
						}
					});
				} else {
					// Not a shared mailbox - return user's email
					resolve(Office.context.mailbox.userProfile.emailAddress);
				}
			} catch (error) {
				console.error("Error getting mailbox owner email:", error);
				// Fallback to user's email
				resolve(Office.context.mailbox.userProfile.emailAddress);
			}
		});
	}

	private logApiResponse(resp: AxiosResponse<any>, fn: string, extraData = {}, message = "") {
		const payload = resp
			? { data: resp?.data, status: resp?.status, url: resp?.config?.url, ...extraData }
			: extraData;
		LogError(new Error(message || "API Response"), payload, fn);
	}

	async sendTaskToXplan(url: string, data: any) {
		try {
			const activeTasks = data["active_tasks"];

			if (activeTasks) {
				const token = await getAccessToken();
				await Promise.all(
					activeTasks.map((at: any) =>
						this.fetchAttachment(url, at?.id, true, "0", token, true)
					)
				);
				// Show success screen after all active tasks are filed
				successScreen(
					false,
					sessionStorage.getItem("redirectUrl"),
					"Go to Tasks",
					"Email filed successfully to active tasks"
				);
			} else {
				const clients = JSON.parse(sessionStorage.getItem("selected_clients"));
				const selected_eIds = clients.map((x: any) => x?.id);
				await this.sendSingleTaskToXplan(url, data, selected_eIds, 0);
			}
		} catch (error) {
			errorScreen(false, null, null, error.message);
			LogError(error, {}, "sendTaskToXplan");
		}
	}

	async sendSingleTaskToXplan(url: string, data: any, selected_eIds: number[], index: number) {
		const payload = await getPayloadAsync(url, null, "POST");

		try {
			data["client"] = selected_eIds[index];
			payload["data"] = data;

			const response = await axios.post(APICalls.AWSUrl(), payload);

			const responseData = handleResponseData(response, "Failed to file to Xplan.");

			if (responseData) {
				await this.fetchAttachment(url, responseData.id, true);
				if (index < selected_eIds.length - 1) {
					const i = index + 1;
					await this.sendSingleTaskToXplan(url, data, selected_eIds, i);
				}
			} else {
				errorScreen(
					false,
					"",
					"",
					"An unexpected error has occured. Please try again later."
				);
			}
		} catch (error) {
			toggleLoading(false);
			errorScreen(false, null, null, error.message);
			LogError(error, { payload: payload }, "sendToXplan", "ERROR");
		}
	}

	private async fetchAttachment(
		url: string,
		docid: string,
		is_task = false,
		existing_doc_id = "0",
		accessToken = "",
		skipSuccessScreen = false
	) {
		const _url = existing_doc_id === "0" ? url + "/" + docid : url;
		const payload = await getPayloadAsync(_url + "/attachment", null, "POST");

		try {
			let _accessToken = accessToken;
			if (!accessToken) _accessToken = await getAccessToken();

			let subject = "";
			if (Office.context.mailbox.item.subject) {
				subject = Office.context.mailbox.item.subject.replace(/[*|?:"<>\\\/]/gi, " ");
			}

			payload["target"] = "EWS";
			payload["ews_method"] = "GetItem";
			payload["accessToken"] = _accessToken;
			payload["restUrl"] = APICalls.MicrosoftGraph;
			payload["itemId"] = trimChar(this.getItemRestId(), "/");
			payload["xplan_url"] = _url + "/attachment";
			payload["is_task"] = is_task;
			payload["subject"] = subject;
			payload["user_id"] = localStorage.getItem("loginUserId");
			payload["user_message_url"] = getXplanURL() + APICalls.UserMessage;
			payload["user_email_address"] = this.getCorrectToAddress();
			payload["mailbox_owner_email"] = await this.getMailboxOwnerEmail();

			const response = await axios.post(APICalls.AWSUrl(), payload);

			this.logApiResponse(response, "FetchAttachment Response");

			const data = handleResponseData(response, "Failed to file to Xplan.");

			if (data) {
				if (data.isError === true) {
					let message = response.data.message;
					if (response.data?.message?.includes(attachnmentTimeout))
						message = message.slice(43);
					LogError(data, { payload: payload }, "getCurrentItems", "ERROR");
					errorScreen(false, "", "", message);
				} else if (!skipSuccessScreen) {
					const title = is_task ? "Go to Tasks" : "Go to Notes";
					successScreen(
						false,
						sessionStorage.getItem("redirectUrl"),
						title,
						data.message
					);
				}
			} else {
				errorScreen(
					false,
					"",
					"",
					"An unexpected error has occured. Please try again later."
				);
			}
		} catch (error) {
			const error_message = error.message;

			if (error.message === "Network Error") {
				const title = is_task ? "Go to Tasks" : "Go to Notes";
				return successScreen(
					false,
					sessionStorage.getItem("redirectUrl"),
					title,
					"Your .eml file may take several minutes to upload"
				);
			}

			if (error.isAxiosError)
				this.logApiResponse(error.response, "FetchAttachment Error", null, error_message);
			else LogError(error, { payload }, "getCurrentItems", "ERROR");

			errorScreen(false, null, null, error_message);
		}
	}

	public setRedirectUrl(xplanUrl: string, entityId: string, type: string, role: string): void {
		const hideUrlFor = localStorage.getItem("hide_url_for") || "";
		const hiddenUrls = appConfig().hidden_urls;
		
		// Try exact match first, then case-insensitive fallback
		let hiddenUrl = hiddenUrls[hideUrlFor];
		if (!hiddenUrl && hideUrlFor) {
			const matchingKey = Object.keys(hiddenUrls).find(
				key => key.toLowerCase() === hideUrlFor.toLowerCase()
			);
			if (matchingKey) {
				hiddenUrl = hiddenUrls[matchingKey];
			}
		}
		
		let _xplanUrl = xplanUrl;

		if (hiddenUrl) {
			if (hiddenUrl.redirect) _xplanUrl = hiddenUrl.redirect;
			else return;
		}

		if (type === "note") {
			const note_url = localStorage.getItem("custom_note_url");
            if (note_url && note_url.trim() !== "") {
			    type = note_url;
            }
		} else if (type === "newtask" || type === "activetask") {
			type = "tasks";

			const task_url = localStorage.getItem("custom_task_url");
			// Only update type if task_url is not empty/null
			if (task_url && task_url.trim() !== "") {
				type = task_url;
			}
		}

		role = role || sessionStorage.getItem("selected_entity_type") || "client";
		const redirectUrl = `${_xplanUrl}/factfind/view/${entityId}?role=${role}&page=${type}`;
		sessionStorage.setItem("redirectUrl", redirectUrl);
	}

	/**
	 * File multiple emails from queue in batch as a single note
	 * Creates ONE document note, then attaches all emails as .eml attachments
	 */
	public async fileQueueToXplan(otherData: any, queuedEmails: any[]): Promise<{
		succeeded: string[];
		failed: Array<{ subject: string; error: string }>;
	}> {
		const succeeded: string[] = [];
		const failed: Array<{ subject: string; error: string }> = [];

		if (queuedEmails.length === 0) {
			return { succeeded, failed };
		}

		try {
			// Step 1: Create an empty note first (this ensures we get a document ID back)
			const firstEmail = queuedEmails[0];
			const noteSubject = otherData.subject || `Batch Filing: ${queuedEmails.length} Emails`;
			const noteId = await this.createEmptyNote(noteSubject, firstEmail, otherData, queuedEmails.length);
			
			if (!noteId) {
				throw new Error("Failed to create note - no document ID returned");
			}

			console.log("Created note with ID:", noteId);

			// Step 2: Attach all emails as .eml attachments to the created note
			for (let i = 0; i < queuedEmails.length; i++) {
				const email = queuedEmails[i];
				try {
					await this.attachEmailToNote(noteId, email);
					succeeded.push(email.subject || "(No Subject)");
				} catch (error) {
					console.error("Error attaching queued email:", email.subject, error);
					failed.push({
						subject: email.subject || "(No Subject)",
						error: error.message || "Failed to attach email",
					});
				}
			}
		} catch (error) {
			// If we fail to create the initial note, mark all emails as failed
			console.error("Error creating note:", error);
			for (let i = 0; i < queuedEmails.length; i++) {
				failed.push({
					subject: queuedEmails[i].subject || "(No Subject)",
					error: i === 0 ? (error.message || "Failed to create note") : "Note creation failed",
				});
			}
		}

		return { succeeded, failed };
	}

	/**
	 * Create an empty document note (without email content)
	 * Returns the created note ID
	 */
	private async createEmptyNote(subject: string, referenceEmail: any, otherData: any, emailCount: number): Promise<string | null> {
		const url = this.url;
		const method = "POST";
		const payload = await getPayloadAsync(url, null, method);

		this.setRedirectUrl(getXplanURL(), otherData.entity_ids[0], otherData.fileToType, otherData.role);

		// Use custom note date if provided, otherwise use reference email date
		const noteDate = otherData.note_date ? new Date(otherData.note_date) : new Date(referenceEmail.dateTimeCreated);

		const data: any = {
			subject,
			body: {
				_type: "Binary",
				_val: btoa(unescape(encodeURIComponent(`Successfully filed ${emailCount} emails. View them in the Attachments.`)))
			},
			ext_type: "OutlookAddInV2", // Link to first email so note appears in Filed Notes
			ext_item_id: referenceEmail.conversationId,
			ext_sub_item_id: referenceEmail.rawItemId, // Use raw itemId
			mimetype: "text/html",
			reference_date: formatDate(noteDate),
			metadata: {
				...this.generateQueuedEmailMetadata(referenceEmail),
				"batch-filing": "true",
				"email-count": String(emailCount)
			},
		};

		// Add filing-specific fields from otherData
		Object.keys(otherData).forEach((key) => {
			if (
				key !== "entity_ids" &&
				key !== "fileToType" &&
				key !== "role" &&
				key !== "subject" &&
				key !== "username" &&
				key !== "note_date"
			) {
				data[key] = otherData[key];
			}
		});

		// Add entity_ids as simple array
		if (otherData.entity_ids && otherData.entity_ids.length > 0) {
			data.entity_ids = otherData.entity_ids.slice();
		}

		payload.data = data;
		payload.target = "XPLAN"; // Use XPLAN target for empty note to get document ID back
		payload.method = method;

		console.log("Creating empty note with payload:", payload);

		// Create the note
		const response = await axios.post(APICalls.AWSUrl(), payload);
		const resData = handleResponseData(response, "Failed to create note");

		console.log("Create note response:", resData);
		console.log("Response data keys:", resData ? Object.keys(resData) : "null");

		if (resData && resData.isError === true) {
			throw new Error(resData.message || "Failed to create note");
		}

		// Update credentials from successful response
		if (resData) {
			updateCredentials(response, resData);
		}

		// The document ID might be in different fields depending on the response
		const noteId = resData?.id || resData?.docid || resData?.doc_id || null;
		
		console.log("Extracted note ID:", noteId);
		
		if (!noteId) {
			console.error("No document ID found in response. Full response:", JSON.stringify(resData));
		}

		// Return the created note ID
		return noteId;
	}

	/**
	 * Attach an email to an existing document note
	 */
	private async attachEmailToNote(noteId: string, email: any): Promise<void> {
		const url = `${this.url}/${noteId}/attachment`;
		const payload = await getPayloadAsync(url, null, "POST");

		const accessToken = await getAccessToken();
		const subject = email.subject || "(No Subject)";

		payload.target = "EWS";
		payload.ews_method = "GetItem";
		payload.accessToken = accessToken;
		payload.restUrl = APICalls.MicrosoftGraph;
		payload.itemId = trimChar(email.itemId, "/");
		payload.xplan_url = url;
		payload.is_task = false;
		payload.subject = subject.replace(/[*|?:"<>\\\/]/gi, " ");
		payload.user_id = getLoginUserId();
		payload.user_message_url = getXplanURL() + APICalls.UserMessage;
		payload.user_email_address = this.getCorrectToAddress();
		payload.mailbox_owner_email = await this.getMailboxOwnerEmail();

		const response = await axios.post(APICalls.AWSUrl(), payload);
		const resData = handleResponseData(response, "Failed to attach email");

		if (resData && resData.isError === true) {
			throw new Error(resData.message || "Failed to attach email");
		}

		// Update credentials from successful response
		if (resData) {
			updateCredentials(response, resData);
		}
	}

	/**
	 * File multiple emails from queue to an EXISTING note
	 * Attaches all emails as .eml attachments to the specified existing note
	 */
	public async fileQueueToExistingNote(noteId: string, otherData: any, queuedEmails: any[]): Promise<{
		succeeded: string[];
		failed: Array<{ subject: string; error: string }>;
	}> {
		const succeeded: string[] = [];
		const failed: Array<{ subject: string; error: string }> = [];

		if (queuedEmails.length === 0) {
			return { succeeded, failed };
		}

		try {
			console.log(`Filing ${queuedEmails.length} emails to existing note ${noteId}`);

			// Set the redirect URL for the success screen
			this.setRedirectUrl(getXplanURL(), otherData.entity_ids[0], 'note', otherData.role);

			// Attach all queued emails as .eml attachments to the existing note
			for (const email of queuedEmails) {
				try {
					await this.attachEmailToNote(noteId, email);
					succeeded.push(email.subject || "(No Subject)");
				} catch (error) {
					console.error(`Error attaching email "${email.subject}" to note ${noteId}:`, error);
					failed.push({
						subject: email.subject || "(No Subject)",
						error: error.message || "Failed to attach email to note",
					});
				}
			}
		} catch (error) {
			// If we fail at the setup stage, mark all emails as failed
			console.error("Error filing to existing note:", error);
			for (const email of queuedEmails) {
				failed.push({
					subject: email.subject || "(No Subject)",
					error: error.message || "Failed to file to existing note",
				});
			}
		}

		return { succeeded, failed };
	}

	/**
	 * Generate metadata for a queued email
	 */
	private generateQueuedEmailMetadata(email: any): Record<string, any> {
		return {
			conversationid: email.conversationId,
			conversationindex: email.rawItemId, // Use raw itemId for metadata
			"content-id": email.rawItemId, // Use raw itemId for metadata
			"email-date": formatDate(new Date(email.dateTimeCreated)),
			"email-from": email.senderEmail,
			"email-from-address": email.senderEmail,
			"email-to": this.arrayToString(email.to, "emailAddress", ";"),
			"email-to-address": this.arrayToString(email.to, "emailAddress", ";"),
			"email-cc": this.arrayToString(email.cc, "emailAddress", ";"),
		};
	}

	/**
	 * File multiple emails from queue to a NEW task
	 * Creates ONE task, then attaches all emails as .eml attachments
	 */
	public async fileQueueToNewTasks(otherData: any, queuedEmails: any[]): Promise<{
		succeeded: string[];
		failed: Array<{ subject: string; error: string }>;
	}> {
		const succeeded: string[] = [];
		const failed: Array<{ subject: string; error: string }> = [];

		if (queuedEmails.length === 0) {
			return { succeeded, failed };
		}

		try {
			// Step 1: Create a new task
			const firstEmail = queuedEmails[0];
			const taskSubject = otherData.subject || `Batch Filing: ${queuedEmails.length} Emails`;
			const taskId = await this.createNewTask(taskSubject, firstEmail, otherData, queuedEmails.length);
			
			if (!taskId) {
				throw new Error("Failed to create task - no task ID returned");
			}

			console.log("Created task with ID:", taskId);

			// Step 2: Attach all emails as .eml attachments to the created task
			for (let i = 0; i < queuedEmails.length; i++) {
				const email = queuedEmails[i];
				try {
					await this.attachEmailToTask(taskId, email);
					succeeded.push(email.subject || "(No Subject)");
				} catch (error) {
					console.error("Error attaching queued email to task:", email.subject, error);
					failed.push({
						subject: email.subject || "(No Subject)",
						error: error.message || "Failed to attach email",
					});
				}
			}
		} catch (error) {
			// If we fail to create the initial task, mark all emails as failed
			console.error("Error creating task:", error);
			for (let i = 0; i < queuedEmails.length; i++) {
				failed.push({
					subject: queuedEmails[i].subject || "(No Subject)",
					error: i === 0 ? (error.message || "Failed to create task") : "Task creation failed",
				});
			}
		}

		return { succeeded, failed };
	}

	/**
	 * File multiple emails from queue to ACTIVE (existing) tasks
	 * Attaches all emails as .eml attachments to each selected active task
	 */
	public async fileQueueToActiveTasks(_otherData: any, queuedEmails: any[]): Promise<{
		succeeded: string[];
		failed: Array<{ subject: string; error: string }>;
	}> {
		const succeeded: string[] = [];
		const failed: Array<{ subject: string; error: string }> = [];

		if (queuedEmails.length === 0) {
			return { succeeded, failed };
		}

		try {
			const activeTasks = JSON.parse(sessionStorage.getItem("selected_active_tasks")) || [];
			
			if (activeTasks.length === 0) {
				throw new Error("No active tasks selected");
			}

			console.log(`Filing ${queuedEmails.length} emails to ${activeTasks.length} active tasks`);

			// Attach all queued emails to each active task
			for (const task of activeTasks) {
				for (const email of queuedEmails) {
					try {
						await this.attachEmailToTask(task.id, email);
						// Count each successful email/task combination
						succeeded.push(email.subject || "(No Subject)");
					} catch (error) {
						console.error(`Error attaching email "${email.subject}" to task ${task.id}:`, error);
						failed.push({
							subject: email.subject || "(No Subject)",
							error: error.message || "Failed to attach email to task",
						});
					}
				}
			}
		} catch (error) {
			// If we fail at the setup stage, mark all emails as failed
			console.error("Error filing to active tasks:", error);
			for (const email of queuedEmails) {
				failed.push({
					subject: email.subject || "(No Subject)",
					error: error.message || "Failed to file to active tasks",
				});
			}
		}

		return { succeeded, failed };
	}

	/**
	 * Create a new task
	 * Returns the created task ID
	 */
	private async createNewTask(subject: string, _referenceEmail: any, otherData: any, emailCount: number): Promise<string | null> {
		const url = this.url;
		const method = "POST";
		const payload = await getPayloadAsync(url, null, method);

		this.setRedirectUrl(getXplanURL(), otherData.entity_ids[0], otherData.fileToType, otherData.role);

		const clients = JSON.parse(sessionStorage.getItem("selected_clients"));
		const selected_eIds = clients.map((x: any) => x?.id);

		const data: any = {
			subject,
			description: `Successfully filed ${emailCount} emails. View them in the Attachments.`,
			taskkind: Number(otherData.tasktype),
			subtype: Number(otherData.tasksubtype),
			status: Number(otherData.taskstatus),
			client: selected_eIds[0], // Use first selected client
		};

		// Add assignee if provided, otherwise backend will assign to current user
		if (otherData.assignee) {
			data.assignee = Number(otherData.assignee);
		}

		payload.data = data;
		payload.target = "XPLAN";
		payload.method = method;

		console.log("Creating new task with payload:", payload);

		// Create the task
		const response = await axios.post(APICalls.AWSUrl(), payload);
		const resData = handleResponseData(response, "Failed to create task");

		console.log("Create task response:", resData);

		if (resData && resData.isError === true) {
			throw new Error(resData.message || "Failed to create task");
		}

		// Update credentials from successful response
		if (resData) {
			updateCredentials(response, resData);
		}

		// The task ID might be in different fields depending on the response
		const taskId = resData?.id || resData?.taskid || resData?.task_id || null;
		
		console.log("Extracted task ID:", taskId);
		
		if (!taskId) {
			console.error("No task ID found in response. Full response:", JSON.stringify(resData));
		}

		return taskId;
	}

	/**
	 * Attach an email to an existing task
	 */
	private async attachEmailToTask(taskId: string, email: any): Promise<void> {
		const url = `${this.url}/${taskId}/attachment`;
		const payload = await getPayloadAsync(url, null, "POST");

		const accessToken = await getAccessToken();
		const subject = email.subject || "(No Subject)";

		payload.target = "EWS";
		payload.ews_method = "GetItem";
		payload.accessToken = accessToken;
		payload.restUrl = APICalls.MicrosoftGraph;
		payload.itemId = trimChar(email.itemId, "/");
		payload.xplan_url = url;
		payload.is_task = true; // Important: set this to true for tasks
		payload.subject = subject.replace(/[*|?:"<>\\\/]/gi, " ");
		payload.user_id = getLoginUserId();
		payload.user_message_url = getXplanURL() + APICalls.UserMessage;
		payload.user_email_address = this.getCorrectToAddress();
		payload.mailbox_owner_email = await this.getMailboxOwnerEmail();

		const response = await axios.post(APICalls.AWSUrl(), payload);
		const resData = handleResponseData(response, "Failed to attach email to task");

		if (resData && resData.isError === true) {
			throw new Error(resData.message || "Failed to attach email to task");
		}

		// Update credentials from successful response
		if (resData) {
			updateCredentials(response, resData);
		}
	}
}
